import { ChangeDetectionStrategy, Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { TasksStatusCardBase } from '@fi-shared/fi-task-shared/tasks-status/common-code/tasks-status-card-base';
import { ITask } from '@fi-shared/fi-task-shared/fi-task-shared';
import { FiDialogsService } from '@fi-shared/fi-dialogs';

@Component({
    selector: 'fi-tasks-status-in-progress',
    template: `
        <mat-card class="fi-content-card">
            <div class="fi-content-card-top fi-tasks-status-card-header">
                {{ 'task.status_in_progress_header' | fiAppText }}
            </div>
            <mat-card-content>
                <button
                    mat-flat-button
                    [disabled]="isActionDisabled()"
                    color="primary"
                    class="fi-tasks-status-action-button"
                    [matMenuTriggerFor]="taskActionMenu"
                >
                    <span>{{ 'common.with_selected' | fiAppText }}</span>
                    <span class="fas fa-chevron-down"></span>
                </button>

                <fi-wrapper-table
                    [items]="tasks"
                    [hasCheckedColumn]="true"
                    matSort
                    #sort="matSort"
                    [sort]="sort"
                    [checkedItems]="selectedTasks"
                    (checkedItemsChange)="selectedTasks = $event"
                >
                    <ng-container matColumnDef="Name">
                        <mat-header-cell *matHeaderCellDef mat-sort-header #headerCell>
                            <fi-wrapper-table-custom-sort-icon
                                sortIconType="string"
                                [ariaSortAttribute]="headerCell._getAriaSortAttribute()"
                                [headerLabel]="'task.task'"
                            >
                            </fi-wrapper-table-custom-sort-icon>
                        </mat-header-cell>
                        <mat-cell *matCellDef="let item" [ngClass]="getTaskClass(item)"> {{ item.Name }} </mat-cell>
                    </ng-container>
                    <ng-container matColumnDef="NumberOfAssignees">
                        <mat-header-cell *matHeaderCellDef mat-sort-header #headerCell>
                            <fi-wrapper-table-custom-sort-icon
                                sortIconType="number"
                                [ariaSortAttribute]="headerCell._getAriaSortAttribute()"
                                [headerLabel]="'task.number_of_assignees'"
                            >
                            </fi-wrapper-table-custom-sort-icon>
                        </mat-header-cell>
                        <mat-cell *matCellDef="let item" [ngClass]="getTaskClass(item)">
                            {{ item.NumberOfAssignees }}
                        </mat-cell>
                    </ng-container>
                    <ng-container matColumnDef="Deadline">
                        <mat-header-cell *matHeaderCellDef mat-sort-header #headerCell>
                            <fi-wrapper-table-custom-sort-icon
                                sortIconType="number"
                                [ariaSortAttribute]="headerCell._getAriaSortAttribute()"
                                [headerLabel]="'task.deadline'"
                            >
                            </fi-wrapper-table-custom-sort-icon>
                        </mat-header-cell>
                        <mat-cell *matCellDef="let item" [ngClass]="getTaskClass(item)">
                            {{ item.Deadline | fiFormatDate: 'date' }}
                        </mat-cell>
                    </ng-container>
                    <ng-container matColumnDef="TimeOverdue">
                        <mat-header-cell *matHeaderCellDef mat-sort-header #headerCell>
                            <fi-wrapper-table-custom-sort-icon
                                sortIconType="number"
                                [ariaSortAttribute]="headerCell._getAriaSortAttribute()"
                                [headerLabel]="'task.time_overdue'"
                            >
                            </fi-wrapper-table-custom-sort-icon>
                        </mat-header-cell>
                        <mat-cell *matCellDef="let item" [ngClass]="getTaskClass(item)">
                            {{ getTimeOverdueText(item) }}
                        </mat-cell>
                    </ng-container>
                    <ng-container matColumnDef="LastReminderSent">
                        <mat-header-cell *matHeaderCellDef mat-sort-header #headerCell>
                            <fi-wrapper-table-custom-sort-icon
                                sortIconType="number"
                                [ariaSortAttribute]="headerCell._getAriaSortAttribute()"
                                [headerLabel]="'common.last_reminder'"
                            >
                            </fi-wrapper-table-custom-sort-icon>
                        </mat-header-cell>
                        <mat-cell *matCellDef="let item" [ngClass]="getTaskClass(item)">
                            {{ item.LastReminderSent | fiFormatDate: 'date-time' }}
                        </mat-cell>
                    </ng-container>
                </fi-wrapper-table>
            </mat-card-content>
        </mat-card>
        <mat-menu #taskActionMenu>
            @for (action of taskActionItems; track action) {
                <button
                    mat-menu-item
                    (click)="action.onClick()"
                    [disabled]="isActionDisabled()"
                    [attr.aria-label]="action.title"
                >
                    <span>{{ action.title }}</span>
                </button>
            }
        </mat-menu>
    `,
    styleUrls: ['./tasks-status-in-progress.component.scss', '../common-code/tasks-status-card-base.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-tasks-status-in-progress' },
    standalone: false,
})
export class TasksStatusInProgressComponent extends TasksStatusCardBase implements OnInit {
    constructor(
        injector: Injector,
        private fiDialogsService: FiDialogsService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.taskActionItems = [
            {
                title: this.appTextService.get('task.archive_tasks'),
                onClick: () => super.saveTaskArchived(true),
            },
            {
                title: this.appTextService.get('task.send_reminders'),
                onClick: () => {
                    this.fiDialogsService
                        .confirm({
                            headerText: this.appTextService.get('task.reminder_confirm_dialog_header'),
                            messageText: this.appTextService.get('task.reminder_confirm_dialog_message'),
                            yesButtonText: this.appTextService.get('task.send_reminders'),
                        })
                        .onYesButtonClick(() => super.sendTaskReminders());
                },
            },
            {
                title: this.appTextService.get('task.set_as_completed'),
                onClick: () => super.saveTaskCompleted(true),
            },
        ];
    }

    getTaskClass(task: ITask) {
        return this.taskHelperService.getTaskClass(task);
    }

    getTimeOverdueText(task: ITask) {
        return this.taskHelperService.getTimeInDays(task.TimeOverdue);
    }
}
